import { Federation } from '@prisma/client'
import { z } from 'zod'
import { AttendeeFormData } from 'domain/models/AttendeeFormData/AttendeeFormData'
import { ImportAttendee } from 'domain/models/Import/ImportAttendee'
import { ImportAttendeeRow } from 'domain/models/Import/ImportAttendeeRow'
import { MINOR_HEALTH_QUESTIONS_KEYS } from 'services/attendees/AttendeeForm/AttendeeFormSchema/HealthQuestionnaireSchema'

const AttendeeFormDataAddressSchema = z
  .object({
    postalAddressMain: z.string(),
    postalAddressSecondary: z.string(),
    postalAddressZipcode: z.string(),
    postalAddressCity: z.string(),
    postalAddressCountry: z.string(),
    postalAddressAreaLevel1: z.string(),
    postalAddressAreaLevel2: z.string()
  })
  .optional()

const AttendeeFormDataParticularsSchema = z.object({
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.coerce.date().optional(),
  gender: z.enum(['MALE', 'FEMALE']).optional(),
  phone: z.string().optional(),
  nationality: z.string().optional(),
  address: AttendeeFormDataAddressSchema
})

const AttendeeFormDataMiscSchema = z
  .object({
    clubOrAssociation: z.string(),
    hideInResults: z.boolean()
  })
  .partial()

const AttendeeFormDataOrganizerSchema = z
  .object({
    paymentStatus: z.enum(['PENDING', 'PAID_MANUALLY', 'FREE']),
    paymentMethod: z.enum(['CASH', 'CHEQUE', 'CREDIT_CARD', 'OTHER'])
  })
  .partial()
  .optional()

const AttendeeFormDataDocumentVerificationSchema = z
  .object({
    verificationStatus: z.enum([
      'VERIFIED',
      'UNVERIFIED',
      'INVALID',
      'REQUIRES_RENEWAL',
      'MANUALLY_VERIFIED'
    ]),
    uploadedDocumentKey: z.string(),
    verifiedAt: z.coerce.date()
  })
  .partial()

export const AttendeeFormDataDocumentSchema = z
  .object({
    completeType: z.enum([
      'LICENSE_MANUALLY_VERIFIED',
      'LICENSE_AUTO_VERIFIED',
      'CHECKBOX_AUTHORIZATION',
      'MEDICAL_CERTIFICATE',
      'PARENTAL_AUTHORIZATION',
      'HEALTH_QUESTIONNAIRE',
      'FFA_PPS',
      'FFA_PPS_TOO_EARLY',
      'FFTRI_BUY_PASS_COMPETITION',
      'NO_SUPPORTING_DOCUMENT_REQUIRED'
    ]),
    federationName: z.nativeEnum(Federation),
    documentNumber: z.string(),
    documentVerification: AttendeeFormDataDocumentVerificationSchema,
    checked: z.boolean(),
    uploadedDocumentKey: z.string(),
    answers: z.record(z.enum(MINOR_HEALTH_QUESTIONS_KEYS), z.boolean()),
    healthInfoFormCompleted: z.boolean(),
    passAdded: z.boolean()
  })
  .partial()
  .optional()

const AttendeeFormDataSchema: z.ZodType<AttendeeFormData> = z.object({
  particulars: AttendeeFormDataParticularsSchema,
  completeLater: z.boolean().optional(),
  document: AttendeeFormDataDocumentSchema,
  misc: AttendeeFormDataMiscSchema,
  organizer: AttendeeFormDataOrganizerSchema
})

const UpdatePaidAttendeeFormDataParticularsSchema = z.object({
  phone: z.string().optional(),
  nationality: z.string().optional(),
  address: AttendeeFormDataAddressSchema
})

export const UpdatePaidAttendeeFormDataSchema = z.object({
  particulars: UpdatePaidAttendeeFormDataParticularsSchema,
  completeLater: z.boolean().optional(),
  options: z.unknown().optional(),
  document: AttendeeFormDataDocumentSchema,
  misc: AttendeeFormDataMiscSchema
})

const ImportOptionValueSchema = z.object({
  key: z.string(),
  childKey: z.string().optional(),
  value: z.union([z.string(), z.boolean()]).optional()
})

const ImportAttendeeRowSchema: z.ZodType<ImportAttendeeRow> = z.object({
  formData: AttendeeFormDataSchema,
  options: z.record(ImportOptionValueSchema),
  raceId: z.number().optional(),
  id: z.string()
})

export const ImportAttendeeSchema: z.ZodType<ImportAttendee> = z.object({
  rows: z.array(ImportAttendeeRowSchema)
})
